import { __decorate } from "tslib";
import React, { isValidElement } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import isString from 'lodash/isString';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import isFunction from 'lodash/isFunction';
import noop from 'lodash/noop';
import defer from 'lodash/defer';
import isPlainObject from 'lodash/isPlainObject';
import { observer } from 'mobx-react';
import { action, computed, isArrayLike, isObservableObject, observable, reaction, runInAction, toJS } from 'mobx';
import classNames from 'classnames';
import Menu, { Item, ItemGroup } from '../../../lib/rc-components/menu';
import Tag from '../../../lib/tag';
import KeyCode from '../../../lib/_util/KeyCode';
import { pxToRem } from '../../../lib/_util/UnitConvertor';
import TriggerField from '../trigger-field/TriggerField';
import autobind from '../_util/autobind';
import Option from '../option/Option';
import OptGroup from '../option/OptGroup';
import { DataSetStatus, FieldType } from '../data-set/enum';
import DataSet from '../data-set/DataSet';
import Spin from '../spin';
import { preventDefault, stopEvent } from '../_util/EventManager';
import normalizeOptions, { OTHER_OPTION_PROPS } from '../option/normalizeOptions';
import { $l } from '../locale-context';
import ObjectChainValue from '../_util/ObjectChainValue';
import isEmpty from '../_util/isEmpty';
import isSame from '../_util/isSame';
import isSameLike from '../_util/isSameLike';
import isIE from '../_util/isIE';
import { getIf } from '../data-set/utils';
import ObserverTextField from '../text-field/TextField';
import Icon from '../icon';
import { ValueChangeAction } from '../text-field/enum';
import { LabelLayout } from '../form/enum';
import { isFieldValueEmpty } from '../field/utils';
function updateActiveKey(menu, activeKey) {
    const store = menu.getStore();
    const menuId = menu.getEventKey();
    const state = store.getState();
    store.setState({
        activeKey: {
            ...state.activeKey,
            [menuId]: activeKey,
        },
    });
}
function defaultSearchMatcher({ record, text, textField }) {
    if (record.get(textField) && isString(record.get(textField))) {
        return record.get(textField).indexOf(text) !== -1;
    }
}
export function isSearchTextEmpty(text) {
    return isArrayLike(text) ? !text.length : !text;
}
export const DISABLED_FIELD = '__disabled';
export const MORE_KEY = '__more__';
function defaultOnOption({ record }) {
    return {
        disabled: record.get(DISABLED_FIELD),
    };
}
export function getItemKey(record, text, value) {
    return `item-${value || record.id}-${(isValidElement(text) ? text.key : text) || record.id}`;
}
function getSimpleValue(value, valueField) {
    if (isPlainObject(value)) {
        return ObjectChainValue.get(value, valueField);
    }
    return value;
}
export class Select extends TriggerField {
    constructor() {
        super(...arguments);
        this.doSearch = debounce((value) => {
            if (isString(this.searchMatcher)) {
                this.searchRemote(value);
            }
            this.handleSearch(value);
        }, 500);
    }
    get searchText() {
        if (this.isSearchFieldInPopup()) {
            return this.$searchText;
        }
        return this.text;
    }
    set searchText(searchText) {
        this.$searchText = searchText;
    }
    get searchMatcher() {
        const { searchMatcher = defaultSearchMatcher } = this.observableProps;
        return searchMatcher;
    }
    get paramMatcher() {
        const { paramMatcher } = this.observableProps;
        return paramMatcher;
    }
    get defaultValidationMessages() {
        const label = this.getProp('label');
        return {
            valueMissing: $l('Select', label ? 'value_missing' : 'value_missing_no_label', { label }),
        };
    }
    get textField() {
        return this.getProp('textField') || 'meaning';
    }
    get valueField() {
        return this.getProp('valueField') || 'value';
    }
    get currentComboOption() {
        const { comboOptions } = this;
        return comboOptions && comboOptions.filter(record => !this.isSelected(record))[0];
    }
    get filteredOptions() {
        const { optionsWithCombo, observableProps: { optionsFilter } } = this;
        return this.searchData(optionsFilter ? optionsWithCombo.filter(optionsFilter) : optionsWithCombo);
    }
    get optionsWithCombo() {
        const { comboOptions } = this;
        return comboOptions ? [...comboOptions.data, ...this.cascadeOptions] : this.cascadeOptions;
    }
    get cascadeOptions() {
        const { record, field, options, searchMatcher } = this;
        const { data } = options;
        if (field && !isString(searchMatcher)) {
            const cascadeMap = field.get('cascadeMap', this.record);
            if (cascadeMap) {
                if (record) {
                    const cascades = Object.keys(cascadeMap);
                    return data.filter(item => cascades.every(cascade => isSameLike(record.get(cascadeMap[cascade]), item.get(cascade))));
                }
                return [];
            }
        }
        return data;
    }
    get searchable() {
        const { searchable = this.getContextConfig('selectSearchable') } = this.observableProps;
        return !!searchable;
    }
    get multiple() {
        return !!this.getProp('multiple');
    }
    get menuMultiple() {
        return this.multiple;
    }
    get options() {
        const { field, textField, valueField, multiple, observableProps: { children, options }, } = this;
        return (options ||
            (field && field.getOptions(this.record)) ||
            normalizeOptions({ textField, valueField, disabledField: DISABLED_FIELD, multiple, children }));
    }
    get primitive() {
        return this.observableProps.primitiveValue !== false && this.getProp('type') !== FieldType.object;
    }
    saveMenu(node) {
        this.menu = node;
    }
    getSearchFieldProps() {
        const { searchFieldProps = {} } = this.props;
        return searchFieldProps;
    }
    isSearchFieldInPopup() {
        const { searchFieldInPopup } = this.props;
        return searchFieldInPopup;
    }
    isEmpty() {
        return (this.isEditableLike() || isEmpty(this.text)) && isFieldValueEmpty(this.getValue(), this.range, this.multiple, this.valueField, this.textField) && this.isRenderEmpty();
    }
    isEditable() {
        return super.isEditable() && ((this.searchable && !this.isSearchFieldInPopup()) || !!this.observableProps.combo);
    }
    checkValue() {
        this.checkValueReaction = reaction(() => this.cascadeOptions, () => this.processSelectedData());
    }
    checkCombo() {
        this.checkComboReaction = reaction(() => this.getValue(), value => this.generateComboOption(value));
    }
    clearCheckValue() {
        if (this.checkValueReaction) {
            this.checkValueReaction();
            delete this.checkValueReaction;
        }
    }
    clearCheckCombo() {
        if (this.checkComboReaction) {
            this.checkComboReaction();
            delete this.checkComboReaction;
        }
    }
    clearReaction() {
        this.clearCheckValue();
        this.clearCheckCombo();
    }
    componentWillMount() {
        const { checkValueOnOptionsChange, combo } = this.props;
        if (checkValueOnOptionsChange) {
            this.checkValue();
        }
        if (combo) {
            this.checkCombo();
            this.generateComboOption(this.getValue());
        }
    }
    componentWillUnmount() {
        super.componentWillUnmount();
        this.doSearch.cancel();
        this.clearReaction();
    }
    componentWillReceiveProps(nextProps, nextContext) {
        super.componentWillReceiveProps(nextProps, nextContext);
        const { checkValueOnOptionsChange, combo } = this.props;
        if (checkValueOnOptionsChange && !nextProps.checkValueOnOptionsChange) {
            this.clearCheckValue();
        }
        if (!checkValueOnOptionsChange && nextProps.checkValueOnOptionsChange) {
            this.checkValue();
        }
        if (combo && !nextProps.combo) {
            this.removeComboOptions();
            this.clearCheckCombo();
        }
        if (!combo && nextProps.combo) {
            this.checkCombo();
            if ('value' in nextProps) {
                this.generateComboOption(nextProps.value);
            }
        }
    }
    componentDidUpdate() {
        this.forcePopupAlign();
    }
    getOmitPropsKeys() {
        return super.getOmitPropsKeys().concat([
            'searchable',
            'searchMatcher',
            'searchFieldInPopup',
            'searchFieldProps',
            'paramMatcher',
            'combo',
            'commonItem',
            'maxCommonTagPlaceholder',
            'maxCommonTagCount',
            'maxCommonTagTextLength',
            'multiple',
            'value',
            'name',
            'options',
            'optionsFilter',
            'dropdownMatchSelectWidth',
            'dropdownMenuStyle',
            'checkValueOnOptionsChange',
            'primitiveValue',
            'optionRenderer',
            'notFoundContent',
            'pagingOptionContent',
            'onOption',
            'noCache',
            'reverse',
            'selectAllButton',
            'optionTooltip',
            'defaultActiveFirstOption',
        ]);
    }
    getObservableProps(props, context) {
        return {
            ...super.getObservableProps(props, context),
            children: props.children,
            options: props.options,
            combo: props.combo,
            commonItem: props.commonItem,
            primitiveValue: props.primitiveValue,
            searchMatcher: props.searchMatcher,
            paramMatcher: props.paramMatcher,
            searchable: props.searchable,
            dropdownMatchSelectWidth: props.dropdownMatchSelectWidth,
            defaultActiveFirstOption: props.defaultActiveFirstOption,
            selectReverse: props.reverse,
            optionsFilter: props.optionsFilter,
        };
    }
    getMenuPrefixCls() {
        return `${this.prefixCls}-dropdown-menu`;
    }
    renderMultipleHolder() {
        const { name, multiple } = this;
        if (multiple) {
            return super.renderMultipleHolder();
        }
        return (React.createElement("input", { key: "value", type: "hidden", value: this.toValueString(this.getValue()) || '', name: name, onChange: noop }));
    }
    getNotFoundContent() {
        const { notFoundContent } = this.props;
        if (notFoundContent !== undefined) {
            return notFoundContent;
        }
        return this.getContextConfig('renderEmpty')('Select');
    }
    getPagingOptionContent() {
        const { pagingOptionContent } = this.props;
        if (pagingOptionContent !== undefined) {
            return pagingOptionContent;
        }
        return this.getContextConfig('selectPagingOptionContent');
    }
    getOtherNextNode() {
        const { commonItem } = this.observableProps;
        if (commonItem) {
            const { options } = this;
            if (!options) {
                return undefined;
            }
            const { textField, valueField, props: { maxCommonTagCount, maxCommonTagPlaceholder, maxCommonTagTextLength }, } = this;
            const values = this.getValues();
            const valueLength = commonItem.length;
            const tags = commonItem.slice(0, maxCommonTagCount).map((item) => {
                let text = item;
                let textRecord;
                options.map((record) => {
                    if (record.get(valueField) === item) {
                        text = maxCommonTagTextLength &&
                            isString(record.get(textField)) &&
                            record.get(textField).length > maxCommonTagTextLength
                            ? `${record.get(textField).slice(0, maxCommonTagTextLength)}...`
                            : record.get(textField);
                        textRecord = record;
                    }
                    return null;
                });
                return (React.createElement(Tag, { key: item, className: values.includes(item) ? `${this.prefixCls}-common-item ${this.prefixCls}-common-item-selected` : `${this.prefixCls}-common-item`, 
                    // @ts-ignore
                    onClick: () => this.handleCommonItemClick(textRecord) }, text));
            });
            if (maxCommonTagCount && valueLength > maxCommonTagCount) {
                let content = `+ ${valueLength - Number(maxCommonTagCount)} ...`;
                if (maxCommonTagPlaceholder) {
                    const omittedValues = commonItem.slice(maxCommonTagCount, valueLength);
                    content =
                        typeof maxCommonTagPlaceholder === 'function'
                            ? maxCommonTagPlaceholder(omittedValues)
                            : maxCommonTagPlaceholder;
                }
                tags.push(React.createElement(Tag, { className: `${this.prefixCls}-common-item`, key: "maxCommonTagPlaceholder" }, content));
            }
            return (React.createElement("div", { className: `${this.prefixCls}-common-item-wrapper` },
                React.createElement("span", { className: `${this.prefixCls}-common-item-label` }, $l('Select', 'common_item')),
                tags));
        }
    }
    getMenuItem({ record, text, value }) {
        const { options, props: { optionRenderer }, } = this;
        return optionRenderer
            ? optionRenderer({ dataSet: options, record, text, value })
            : text;
    }
    getMenu(menuProps = {}) {
        const { options, } = this;
        if (!options) {
            return null;
        }
        const { getTooltip, getTooltipTheme, getTooltipPlacement } = this.context;
        const { disabled: menuDisabled, textField, valueField, props: { dropdownMenuStyle, onOption, optionTooltip = getTooltip('select-option') }, } = this;
        const groups = options.getGroups();
        const optGroups = [];
        const selectedKeys = [];
        /**
         * fixed when ie the scroll width would cover the item width
         */
        const IeMenuStyle = !this.dropdownMatchSelectWidth && isIE() ? { padding: '.08rem' } : {};
        const IeItemStyle = !this.dropdownMatchSelectWidth && isIE() ? { overflow: 'visible' } : {};
        this.filteredOptions.forEach(record => {
            let previousGroup;
            groups.every(field => {
                const label = record.get(field);
                if (label !== undefined) {
                    if (!previousGroup) {
                        previousGroup = optGroups.find(item => item.props.title === label);
                        if (!previousGroup) {
                            previousGroup = (React.createElement(ItemGroup, { key: `group-${label}`, title: label }, []));
                            optGroups.push(previousGroup);
                        }
                    }
                    else {
                        const { children } = previousGroup.props;
                        previousGroup = children.find(item => item.props.title === label);
                        if (!previousGroup) {
                            previousGroup = (React.createElement(ItemGroup, { key: `group-${label}`, title: label }, []));
                            children.push(previousGroup);
                        }
                    }
                    return true;
                }
                return false;
            });
            const value = record.get(valueField);
            const text = record.get(textField);
            const optionProps = onOption({ dataSet: options, record });
            const key = getItemKey(record, text, value);
            if (!('selectedKeys' in menuProps) && this.isSelected(record)) {
                selectedKeys.push(key);
            }
            const itemContent = this.getMenuItem({ record, text, value });
            const recordValues = record.get(OTHER_OPTION_PROPS) || {};
            const itemProps = {
                ...recordValues,
                style: {
                    ...IeItemStyle,
                    ...recordValues.style,
                },
                key,
                value: record,
                disabled: menuDisabled,
                tooltip: optionTooltip,
                tooltipTheme: getTooltipTheme('select-option'),
                tooltipPlacement: getTooltipPlacement('select-option'),
            };
            const mergedProps = optionProps ? {
                ...optionProps,
                ...itemProps,
                className: classNames(optionProps.className, itemProps.className, {
                    [`${this.prefixCls}-current`]: record.isCurrent,
                }),
                style: {
                    ...optionProps.style,
                    ...itemProps.style,
                },
                disabled: itemProps.disabled || optionProps.disabled,
            } : itemProps;
            const option = (React.createElement(Item, Object.assign({}, mergedProps), toJS(itemContent)));
            if (previousGroup) {
                const { children } = previousGroup.props;
                children.push(option);
            }
            else {
                optGroups.push(option);
            }
        });
        if (!optGroups.length) {
            optGroups.push(React.createElement(Item, { key: "no_data", disabled: true, checkable: false }, this.loading ? ' ' : this.getNotFoundContent()));
        }
        const menuPrefix = this.getMenuPrefixCls();
        return (React.createElement(Menu, Object.assign({ ref: this.saveMenu, disabled: menuDisabled, defaultActiveFirst: this.defaultActiveFirstOption, multiple: this.menuMultiple, selectedKeys: selectedKeys, prefixCls: menuPrefix, onClick: this.handleMenuClick, onMouseDown: preventDefault, style: { ...IeMenuStyle, ...dropdownMenuStyle }, focusable: false }, menuProps),
            optGroups,
            options.paging && options.currentPage < options.totalPage && (React.createElement(Item, { key: MORE_KEY, checkable: false, className: `${menuPrefix}-item-more` }, this.getPagingOptionContent()))));
    }
    /**
     * 增加lov popupContent 回调参数 用于控制对应交互
     */
    getPopupProps() {
        const { options, textField, field, record, valueField } = this;
        return {
            ...super.getPopupProps(),
            dataSet: options,
            textField,
            valueField,
            field,
            record,
            content: this.getPopupContent(),
        };
    }
    getPopupClassName(defaultClassName) {
        const { multiple, prefixCls } = this;
        return classNames(defaultClassName, {
            [`${prefixCls}-popup-multiple`]: multiple,
            [`${prefixCls}-popup-single`]: !multiple,
        });
    }
    get dropdownMatchSelectWidth() {
        const { dropdownMatchSelectWidth = this.getContextConfig('dropdownMatchSelectWidth') } = this.observableProps;
        return dropdownMatchSelectWidth;
    }
    get defaultActiveFirstOption() {
        const { defaultActiveFirstOption = this.getContextConfig('defaultActiveFirstOption') } = this.observableProps;
        return defaultActiveFirstOption;
    }
    get selectReverse() {
        const { selectReverse = this.getContextConfig('selectReverse') } = this.observableProps;
        return selectReverse;
    }
    get loading() {
        return this.options.status === DataSetStatus.loading;
    }
    handlePopupSearch(value) {
        this.searchText = value;
        this.doSearch(value);
    }
    renderSearchFieldPrefix(_props) {
        return undefined;
    }
    renderSearchField(props) {
        const { searchText, prefixCls } = this;
        const searchFieldProps = this.getSearchFieldProps();
        const { multiple, className } = searchFieldProps;
        return (React.createElement("div", { className: `${prefixCls}-search-bar` },
            this.renderSearchFieldPrefix(props),
            React.createElement(ObserverTextField, Object.assign({ value: searchText, onChange: this.handlePopupSearch, prefix: React.createElement(Icon, { type: "search" }), valueChangeAction: multiple ? ValueChangeAction.blur : ValueChangeAction.input, labelLayout: LabelLayout.none, border: false }, searchFieldProps, { className: classNames(`${prefixCls}-search-field`, className) }))));
    }
    renderSelectAll() {
        const { selectAllButton } = this.props;
        if (this.multiple && selectAllButton) {
            const builtInButtons = [
                { key: 'select_all', onClick: this.chooseAll, children: $l('Select', 'select_all') },
            ];
            if (this.selectReverse) {
                builtInButtons.push({ key: 'select_re', onClick: this.chooseRe, children: $l('Select', 'select_re') });
            }
            builtInButtons.push({ key: 'unselect_all', onClick: this.unChooseAll, children: $l('Select', 'unselect_all') });
            const buttons = typeof selectAllButton === 'function' ? selectAllButton(builtInButtons) : builtInButtons;
            return (React.createElement("div", { key: "check-all", className: `${this.prefixCls}-select-all-none` }, buttons.map(({ key, onClick, children }, index) => (React.createElement("span", { key: key || String(index), onClick: onClick }, children)))));
        }
    }
    getPopupContent() {
        const menu = (React.createElement(Spin, { key: "menu", spinning: this.loading }, this.getMenu()));
        return [
            this.searchable && this.isSearchFieldInPopup() && this.renderSearchField(),
            this.renderSelectAll(),
            menu,
        ];
    }
    getPopupStyleFromAlign(target) {
        if (target) {
            const width = pxToRem(target.getBoundingClientRect().width);
            if (width !== undefined) {
                const { isFlat } = this.props;
                if (!isFlat && this.dropdownMatchSelectWidth) {
                    return {
                        width,
                    };
                }
                return {
                    minWidth: width,
                };
            }
        }
    }
    getDefaultAction() {
        return this.getContextConfig('selectTrigger') || super.getDefaultAction();
    }
    getTriggerIconFont() {
        return this.searchable && !this.isSearchFieldInPopup() && this.isFocused && !this.readOnly ? 'search' : 'baseline-arrow_drop_down';
    }
    handleKeyDown(e) {
        const { menu } = this;
        /**
         * 修复ie出现点击backSpace的页面回到上一页问题
         */
        if (isIE()) {
            if (e.keyCode === KeyCode.BACKSPACE) {
                e.preventDefault();
            }
        }
        if (!this.disabled && !this.readOnly && menu) {
            if (this.popup && menu.onKeyDown(e)) {
                stopEvent(e);
            }
            else {
                switch (e.keyCode) {
                    case KeyCode.RIGHT:
                    case KeyCode.DOWN:
                        this.handleKeyDownPrevNext(e, menu, 1);
                        break;
                    case KeyCode.LEFT:
                    case KeyCode.UP:
                        this.handleKeyDownPrevNext(e, menu, -1);
                        break;
                    case KeyCode.END:
                    case KeyCode.PAGE_DOWN:
                        this.handleKeyDownFirstLast(e, menu, 1);
                        break;
                    case KeyCode.HOME:
                    case KeyCode.PAGE_UP:
                        this.handleKeyDownFirstLast(e, menu, -1);
                        break;
                    // case KeyCode.ENTER:
                    //   this.handleKeyDownEnter(e);
                    //   break;
                    case KeyCode.ESC:
                        this.handleKeyDownEsc(e);
                        break;
                    case KeyCode.SPACE:
                        this.handleKeyDownSpace(e);
                        break;
                    default:
                }
            }
        }
        super.handleKeyDown(e);
    }
    isMultipleBlockDisabled(v) {
        const { props: { onOption }, } = this;
        const findRecord = this.findByValue(v);
        const optionProps = findRecord ? onOption === defaultOnOption ? defaultOnOption({ record: findRecord }) : onOption({
            dataSet: this.options,
            record: findRecord,
        }) : undefined;
        const optionDisabled = (optionProps && optionProps.disabled);
        return (findRecord && findRecord.get(DISABLED_FIELD) === true) || optionDisabled || this.disabled;
    }
    handleKeyDownFirstLast(e, menu, direction) {
        stopEvent(e);
        // TreeSelect event conflict
        if (!menu.tree) {
            const children = menu.getFlatInstanceArray();
            const activeItem = children[direction < 0 ? 0 : children.length - 1];
            if (activeItem) {
                if (!this.editable || this.popup) {
                    updateActiveKey(menu, activeItem.props.eventKey);
                }
                if (!this.editable && !this.popup) {
                    this.choose(activeItem.props.value);
                }
            }
        }
    }
    handleKeyDownPrevNext(e, menu, direction) {
        if (!this.multiple && !this.editable && !menu.tree) {
            const activeItem = menu.step(direction);
            if (activeItem) {
                updateActiveKey(menu, activeItem.props.eventKey);
                this.choose(activeItem.props.value);
            }
            e.preventDefault();
        }
        else if (e === KeyCode.DOWN) {
            this.expand();
            e.preventDefault();
        }
    }
    // handleKeyDownEnter(_e) {
    // }
    handleKeyDownEsc(e) {
        if (this.popup) {
            e.preventDefault();
            this.collapse();
        }
    }
    handleKeyDownSpace(e) {
        if (!this.editable) {
            e.preventDefault();
            if (!this.popup) {
                this.expand();
            }
        }
    }
    handleBlur(e) {
        if (!e.isDefaultPrevented()) {
            super.handleBlur(e);
            this.resetFilter();
        }
    }
    expand() {
        const { filteredOptions } = this;
        if (filteredOptions && filteredOptions.length) {
            super.expand();
        }
    }
    syncValueOnBlur(value) {
        if (value) {
            const { comboOptions } = this;
            const data = comboOptions ? comboOptions.data : [];
            this.options.ready().then(() => {
                const record = this.findByTextWithValue(value, data);
                if (record) {
                    this.choose(record);
                }
            });
        }
        else if (!this.multiple) {
            this.setValue(this.emptyValue);
        }
    }
    findByTextWithValue(text, data) {
        const { textField } = this;
        const records = [...data, ...this.filteredOptions].filter(record => isSameLike(record.get(textField), text));
        if (records.length > 1) {
            const { valueField, primitive } = this;
            const value = this.getValue();
            if (value) {
                const found = records.find(record => isSameLike(record.get(valueField), primitive ? value : value[valueField]));
                if (found) {
                    return found;
                }
            }
        }
        return records[0];
    }
    findByText(text) {
        const { textField } = this;
        return this.optionsWithCombo.find(record => isSameLike(record.get(textField), text));
    }
    findByValue(value) {
        const { valueField } = this;
        const autoType = this.getProp('type') === FieldType.auto;
        value = getSimpleValue(value, valueField);
        return this.optionsWithCombo.find(record => autoType ? isSameLike(record.get(valueField), value) : isSame(record.get(valueField), value));
    }
    isSelected(record) {
        const { valueField } = this;
        const autoType = this.getProp('type') === FieldType.auto;
        return this.getValues().some(value => {
            const simpleValue = getSimpleValue(value, valueField);
            return autoType
                ? isSameLike(record.get(valueField), simpleValue)
                : isSame(record.get(valueField), simpleValue);
        });
    }
    generateComboOption(value, callback) {
        const { currentComboOption, textField, valueField } = this;
        if (value) {
            if (isArrayLike(value)) {
                value.forEach(v => !isNil(v) && this.generateComboOption(v));
            }
            else {
                const found = this.findByText(value) || this.findByValue(value);
                if (found) {
                    const text = found.get(textField);
                    if (text !== value && callback) {
                        callback(text);
                    }
                    this.removeComboOption();
                }
                else if (currentComboOption) {
                    currentComboOption.set(textField, value);
                    currentComboOption.set(valueField, value);
                }
                else {
                    this.createComboOption(value);
                }
            }
        }
        else {
            this.removeComboOption();
        }
    }
    createComboOption(value) {
        const { textField, valueField, menu } = this;
        const comboOptions = getIf(this, 'comboOptions', () => new DataSet());
        const initData = !this.primitive && isObservableObject(value) ? value : {
            [textField]: value,
            [valueField]: value,
        };
        const record = comboOptions.create(initData, 0);
        if (menu) {
            updateActiveKey(menu, getItemKey(record, initData[valueField], initData[valueField]));
        }
    }
    removeComboOptions() {
        const { comboOptions } = this;
        if (comboOptions) {
            comboOptions.forEach(record => this.removeComboOption(record));
        }
    }
    removeComboOption(record) {
        if (!record) {
            record = this.currentComboOption;
        }
        if (record && !this.isSelected(record)) {
            const { comboOptions } = this;
            if (comboOptions) {
                comboOptions.remove(record);
            }
        }
    }
    handlePopupAnimateAppear() {
        // noop
    }
    getValueKey(v) {
        if (isArrayLike(v)) {
            return v.map(this.getValueKey, this).join(',');
        }
        const autoType = this.getProp('type') === FieldType.auto;
        const value = getSimpleValue(v, this.valueField);
        return autoType && !isNil(value) ? value.toString() : value;
    }
    handlePopupAnimateEnd(_key, _exists) {
        // noop
    }
    handleMenuClick({ key, item: { props: { value }, }, }) {
        if (key === MORE_KEY) {
            this.options.queryMore(this.options.currentPage + 1);
        }
        else if (this.multiple && this.isSelected(value)) {
            this.unChoose(value);
        }
        else {
            this.choose(value);
        }
    }
    handleCommonItemClick(value) {
        if (this.multiple && this.isSelected(value)) {
            this.unChoose(value);
        }
        else {
            this.choose(value);
        }
    }
    handleOptionSelect(record) {
        this.prepareSetValue(...(isArrayLike(record) ? record.map(this.processRecordToObject, this) : [this.processRecordToObject(record)]));
    }
    handleOptionUnSelect(record) {
        const { valueField } = this;
        const newValues = isArrayLike(record) ? record.map(r => r.get(valueField)) : [record.get(valueField)];
        this.removeValues(newValues, -1);
    }
    handleSearch(_text) {
        // noop
    }
    setText(text) {
        super.setText(text);
        if (this.searchable && !this.isSearchFieldInPopup()) {
            this.doSearch(text);
        }
    }
    getSearchPara(searchMatcher, value) {
        const { paramMatcher } = this;
        if (isString(paramMatcher)) {
            if (isArrayLike(value)) {
                return { [searchMatcher]: value.map(v => `${v || ''}${paramMatcher}`) };
            }
            return { [searchMatcher]: `${value || ''}${paramMatcher}` };
        }
        if (isFunction(paramMatcher)) {
            const { record, textField, valueField } = this;
            const searchPara = paramMatcher({ record, text: value, textField, valueField, key: searchMatcher });
            if (searchPara) {
                if (isString(searchPara)) {
                    return { [searchMatcher]: value };
                }
                return searchPara;
            }
        }
        return { [searchMatcher]: value };
    }
    searchRemote(text) {
        const { field, searchMatcher, record } = this;
        if (field && isString(searchMatcher)) {
            const searchPara = this.getSearchPara(searchMatcher, text);
            const optionDs = field.get('options', record);
            Object.keys(searchPara).forEach(key => {
                const value = searchPara[key];
                const lovPara = value === '' ? undefined : value;
                if (optionDs) {
                    optionDs.query(undefined, { [key]: lovPara });
                }
                else {
                    field.setLovPara(key, lovPara);
                }
            });
        }
    }
    /**
     * 该方法会被onChange和onCompositionend触发
     * @param e 改变事件
     */
    handleChange(e) {
        const { target, target: { value }, type, } = e;
        const restricted = this.restrictInput(value);
        if (restricted !== value) {
            const selectionEnd = target.selectionEnd + restricted.length - value.length;
            target.value = restricted;
            target.setSelectionRange(selectionEnd, selectionEnd);
        }
        this.setText(restricted);
        if (this.observableProps.combo) {
            if (type !== 'compositionend') {
                this.generateComboOption(restricted, text => this.setText(text));
            }
        }
        if (!this.popup) {
            this.expand();
        }
    }
    processRecordToObject(record) {
        const { primitive, valueField } = this;
        // 如果为原始值那么 restricted 失效
        const restricted = this.restrictInput(record.get(valueField));
        return primitive ? restricted : record.toData();
    }
    processObjectValue(value, textField) {
        if (!isNil(value)) {
            if (isPlainObject(value)) {
                return ObjectChainValue.get(value, textField);
            }
            const found = this.findByValue(value);
            if (found) {
                return found.get(textField);
            }
        }
    }
    processLookupValue(value) {
        const { field, textField, primitive, record } = this;
        if (primitive && field && field.getLookup(record)) {
            return super.processValue(field.getText(value, undefined, record));
        }
        return super.processValue(this.processObjectValue(value, textField));
    }
    processValue(value) {
        const text = this.processLookupValue(value);
        if (isEmpty(text)) {
            if (isPlainObject(value)) {
                return ObjectChainValue.get(value, this.valueField) || '';
            }
            return super.processValue(value);
        }
        return text;
    }
    clear() {
        const values = this.getValues();
        const valueLength = values.length;
        const { props: { maxTagCount = valueLength, onClear = noop, onOption = noop }, options, } = this;
        this.setText(undefined);
        if (this.multiple) {
            const valuesDisabled = values.slice(0, maxTagCount).filter(v => {
                const recordItem = this.findByValue(v);
                const findRecord = this.findByValue(v);
                const optionProps = findRecord ? onOption({ dataSet: options, record: findRecord }) : undefined;
                const optionDisabled = (optionProps && optionProps.disabled);
                return (recordItem && recordItem.get(DISABLED_FIELD) === true) || optionDisabled;
            });
            const multipleValue = valuesDisabled.length > 0 ? valuesDisabled : this.emptyValue;
            this.setValue(multipleValue);
        }
        else {
            this.setValue(this.emptyValue);
        }
        this.rangeValue = this.isFocused ? [undefined, undefined] : undefined;
        onClear();
        this.removeComboOptions();
    }
    // 当触发清空操作时候会导致两次触发onchange可搜索不需要设置值
    setRangeTarget(target) {
        if (this.text !== undefined) {
            if (!this.searchable || this.isSearchFieldInPopup()) {
                this.prepareSetValue(this.text);
            }
            this.setText();
        }
        super.setRangeTarget(target);
        defer(() => this.isFocused && this.select());
    }
    resetFilter() {
        this.setText(undefined);
        this.removeComboOption();
        this.forcePopupAlign();
    }
    reset() {
        super.reset();
        this.resetFilter();
    }
    unChoose(record) {
        if (record) {
            this.handleOptionUnSelect(record);
        }
    }
    choose(record) {
        if (!this.multiple) {
            this.collapse();
        }
        if (record) {
            this.handleOptionSelect(record);
        }
    }
    chooseAll() {
        const { options, props: { onOption }, } = this;
        const selectedOptions = this.filteredOptions.filter((record) => {
            const optionProps = onOption({ dataSet: options, record });
            const optionDisabled = (optionProps && optionProps.disabled);
            return !optionDisabled;
        });
        this.choose(selectedOptions);
    }
    /**
     * 反选
     */
    chooseRe() {
        const { options, valueField, props: { onOption }, } = this;
        const values = this.getValues();
        const selectedOptions = this.filteredOptions.filter((record) => {
            const optionProps = onOption({ dataSet: options, record });
            const value = record.get(valueField);
            const optionDisabled = (optionProps && optionProps.disabled);
            const optionIsSelect = values.some((v) => {
                if (typeof v === 'object') {
                    return v[valueField] === value;
                }
                return v === value;
            });
            return (!optionDisabled && !optionIsSelect) || (optionDisabled && optionIsSelect);
        });
        this.setValue(selectedOptions.map(this.processRecordToObject, this));
    }
    unChooseAll() {
        this.clear();
    }
    handlePopupHiddenChange(hidden) {
        const noCache = this.getProp('noCache');
        if (!hidden) {
            const { field } = this;
            if (field) {
                field.fetchLookup(noCache, this.record);
            }
            this.forcePopupAlign();
        }
        super.handlePopupHiddenChange(hidden);
    }
    async processSelectedData() {
        const { comboOptions } = this;
        if (comboOptions) {
            comboOptions.removeAll();
        }
        const values = this.getValues();
        const { field } = this;
        if (field) {
            await field.ready();
        }
        const { filteredOptions, observableProps: { combo }, } = this;
        runInAction(() => {
            const newValues = values.filter(value => {
                const record = this.findByValue(value);
                if (record) {
                    return true;
                }
                if (combo) {
                    this.createComboOption(value);
                    return true;
                }
                return false;
            });
            if (this.text && combo) {
                this.generateComboOption(this.text);
            }
            if (field &&
                field.get('cascadeMap', this.record) &&
                filteredOptions.length &&
                !isEqual(newValues, values)) {
                this.setValue(this.multiple ? newValues : newValues[0]);
            }
        });
    }
    searchData(data) {
        const { searchable, searchMatcher, searchText, } = this;
        return searchable && typeof searchMatcher === 'function' && !isSearchTextEmpty(searchText) ? data.filter((r) => this.matchRecordBySearch(r, searchText)) : data;
    }
    matchRecordBySearch(record, text) {
        const { textField, valueField, searchable, searchMatcher, } = this;
        return !searchable || typeof searchMatcher !== 'function' || searchMatcher({
            record,
            text,
            textField,
            valueField,
            value: record.get(valueField),
            props: record.get(OTHER_OPTION_PROPS),
        });
    }
    renderLengthInfo(_maxLength) {
        return undefined;
    }
}
Select.displayName = 'Select';
Select.propTypes = {
    /**
     * 复合输入值
     * @default false
     */
    combo: PropTypes.bool,
    /**
     * 常用项
     * @default undefined
     */
    commonItem: PropTypes.array,
    /**
     * 多值标签超出最大数量时的占位描述
     */
    maxCommonTagPlaceholder: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
    /**
     * 多值标签最大数量
     */
    maxCommonTagCount: PropTypes.number,
    /**
     * 多值标签文案最大长度
     */
    maxCommonTagTextLength: PropTypes.number,
    /**
     * 过滤器
     * @default false
     */
    searchable: PropTypes.bool,
    /**
     * 搜索匹配器。 当为字符串时，作为lookup的参数名来重新请求值列表。
     */
    searchMatcher: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    /**
     * 参数匹配器。 当为字符串时，参数拼接。
     */
    paramMatcher: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    /**
     * 是否为原始值
     * true - 选项中valueField对应的值
     * false - 选项值对象
     */
    primitiveValue: PropTypes.bool,
    /**
     * 渲染Option文本的钩子
     * @example
     * ```js
     * <Select
     *   {...props}
     *   optionRenderer={({ dataSet, record, text, value }) => text + '$'}
     * />
     * ```
     */
    optionRenderer: PropTypes.func,
    /**
     * 当下拉列表为空时显示的内容
     */
    notFoundContent: PropTypes.node,
    /**
     * 渲染分页 Item 内容
     */
    pagingOptionContent: PropTypes.node,
    /**
     * 设置选项属性，如 disabled;
     */
    onOption: PropTypes.func,
    /**
     * 下拉时自动重新查询
     */
    noCache: PropTypes.bool,
    /**
     * 下拉框匹配输入框宽度
     * @default true
     */
    dropdownMatchSelectWidth: PropTypes.bool,
    /**
     * 多选时显示全选按钮;
     * @default true
     */
    selectAllButton: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
    /**
     * 多选是否开启反选
     * @default false
     */
    reverse: PropTypes.bool,
    /**
     * 用tooltip显示选项内容
     * 可选值：`none` `always` `overflow`
     */
    optionTooltip: PropTypes.string,
    /**
     * 是否默认高亮第一个选项
     * @default true
     */
    defaultActiveFirstOption: PropTypes.bool,
    ...TriggerField.propTypes,
};
Select.defaultProps = {
    ...TriggerField.defaultProps,
    suffixCls: 'select',
    combo: false,
    checkValueOnOptionsChange: true,
    onOption: defaultOnOption,
    selectAllButton: true,
};
Select.Option = Option;
Select.OptGroup = OptGroup;
Select.__PRO_SELECT = true;
__decorate([
    observable
], Select.prototype, "comboOptions", void 0);
__decorate([
    observable
], Select.prototype, "$searchText", void 0);
__decorate([
    computed
], Select.prototype, "filteredOptions", null);
__decorate([
    computed
], Select.prototype, "optionsWithCombo", null);
__decorate([
    computed
], Select.prototype, "cascadeOptions", null);
__decorate([
    computed
], Select.prototype, "options", null);
__decorate([
    autobind
], Select.prototype, "saveMenu", null);
__decorate([
    autobind
], Select.prototype, "getMenu", null);
__decorate([
    autobind
], Select.prototype, "getPopupProps", null);
__decorate([
    autobind,
    action
], Select.prototype, "handlePopupSearch", null);
__decorate([
    autobind
], Select.prototype, "renderSearchField", null);
__decorate([
    autobind
], Select.prototype, "getPopupStyleFromAlign", null);
__decorate([
    autobind
], Select.prototype, "handleKeyDown", null);
__decorate([
    autobind
], Select.prototype, "isMultipleBlockDisabled", null);
__decorate([
    autobind
], Select.prototype, "handleBlur", null);
__decorate([
    action
], Select.prototype, "createComboOption", null);
__decorate([
    autobind
], Select.prototype, "getValueKey", null);
__decorate([
    autobind
], Select.prototype, "handlePopupAnimateEnd", null);
__decorate([
    autobind
], Select.prototype, "handleMenuClick", null);
__decorate([
    autobind
], Select.prototype, "handleCommonItemClick", null);
__decorate([
    action
], Select.prototype, "setText", null);
__decorate([
    autobind,
    action
], Select.prototype, "handleChange", null);
__decorate([
    action
], Select.prototype, "clear", null);
__decorate([
    autobind
], Select.prototype, "reset", null);
__decorate([
    autobind
], Select.prototype, "chooseAll", null);
__decorate([
    autobind
], Select.prototype, "chooseRe", null);
__decorate([
    autobind
], Select.prototype, "unChooseAll", null);
__decorate([
    autobind
], Select.prototype, "handlePopupHiddenChange", null);
__decorate([
    autobind
], Select.prototype, "matchRecordBySearch", null);
let ObserverSelect = class ObserverSelect extends Select {
};
ObserverSelect.defaultProps = Select.defaultProps;
ObserverSelect.Option = Option;
ObserverSelect.OptGroup = OptGroup;
ObserverSelect.__PRO_SELECT = true;
ObserverSelect = __decorate([
    observer
], ObserverSelect);
export default ObserverSelect;
